import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type { IContact } from '../contacts.types';

const CONTACT_API = {
  list: 'api/contacts/list',
  add: 'api/contacts',
  update: 'api/contacts',
  details: 'api/contacts/details',
  delete: 'api/contacts',
  import: 'api/contacts/import',
  export: 'api/contacts/export',
  merge: 'api/contacts/merge',
  split: 'api/contacts/split',
  mergeHistory: 'api/contacts/merge-history',
};

export const contactsApi = createApi({
  reducerPath: 'contactsApi',
  baseQuery,
  endpoints: (builder) => ({
    // ----------------------------------------------------------------------
    // NOTE FOR DEVELOPERS:
    // Please maintain the following order for endpoints ensuring CRUD consistency:
    // 1. List (Filter)
    // 2. Details
    // 3. Create
    // 4. Update
    // 5. Delete
    // 6. Import
    // 7. Export
    // ----------------------------------------------------------------------

    filterContact: builder.mutation({
      query: (filterQuery) => ({
        url: CONTACT_API.list,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { list?: IContact[]; total?: number } }) => {
        const list = response?.data?.list ?? [];

        const customFieldObj = (obj: any) => {
          const cfObj = obj?.customFields?.reduce(
            (acc: any, cf: any) => {
              acc[cf.id] = cf.values;
              return acc;
            },
            {} as Record<string, any>
          );

          return cfObj || {};
        };

        const obj = {
          list: list?.map((item) => ({ ...item, customFields: customFieldObj(item) })) ?? [],
          total: response?.data?.total ?? 0,
        };

        return obj;
      },
    }),

    getContactDtl: builder.mutation({
      query: (id: string) => ({
        url: `${CONTACT_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data: IContact }) => ({
        data: { contact: response.data },
      }),
    }),

    createContact: builder.mutation<any, Partial<any>>({
      query: (newAccount) => ({
        url: CONTACT_API.add,
        method: 'POST',
        body: newAccount,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { contact: IContact };
        success: boolean;
      }): IContact =>
        response.success && response.data?.contact ? response.data.contact : ({} as IContact),
    }),

    updateContact: builder.mutation<IContact, { id: string; updates: Partial<IContact> }>({
      query: ({ id, updates }) => ({
        url: `${CONTACT_API.update}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    deleteContacts: builder.mutation({
      query: (ids: string[]) => ({
        url: CONTACT_API.delete,
        method: 'DELETE',
        body: { ids },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    importContact: builder.mutation({
      query: (filePath) => ({
        url: CONTACT_API.import,
        method: 'POST',
        body: filePath,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    exportcontact: builder.mutation({
      query: (filter) => ({
        url: CONTACT_API.export,
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    mergeContacts: builder.mutation<
      any,
      { primaryContactId: string; secondaryContactIds: string[] }
    >({
      query: (body) => ({
        url: CONTACT_API.merge,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    splitContact: builder.mutation<any, { mergeLogId: string }>({
      query: (body) => ({
        url: CONTACT_API.split,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    getContactMergeHistory: builder.query<any, { id: string; page?: number; limit?: number }>({
      query: ({ id, page = 1, limit = 10 }) => ({
        url: `${CONTACT_API.mergeHistory}/${id}`,
        method: 'GET',
        params: { page, limit },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useFilterContactMutation,
  useGetContactDtlMutation,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactsMutation,
  useImportContactMutation,
  useExportcontactMutation,
  useMergeContactsMutation,
  useSplitContactMutation,
  useGetContactMergeHistoryQuery,
} = contactsApi;
